import type { CascaderOptionType } from 'antd/lib/cascader';
import { sortBy } from 'ramda';
import type { CSSProperties } from 'react';
import { connect } from 'react-redux';

import type { ProcessType, SupplierMaterial } from '@frontend/api';
import { Select } from '@frontend/components';

import { getMaterialOptionsForCascaderForSupplierMaterials } from 'store/materials/reducer';

import MaterialSelector from './MaterialSelector';

const sortByName = sortBy((mat: { supplierMaterial: SupplierMaterial }) =>
  mat.supplierMaterial.displayName.toLowerCase(),
);

type ProcessMaterialSelectorProps = {
  cascaderOptions?: CascaderOptionType[];
  disabled: boolean;
  menuPortalDestinationRef: React.RefObject<HTMLDivElement>;
  onChange: ({
    materialId,
    supplierMaterialId,
  }: {
    materialId: number | string | null;
    supplierMaterialId: number | string | null;
  }) => void;
  placeholder: string;
  process?: ProcessType;
  selectedMaterialId: number | null;
  selectedSupplierMaterialId: number | string | null;
};

type ProcessMaterialSelectorOption = {
  supplierMaterialId: number;
  materialId: number;
  label: string;
};

const connector = connect(
  (state, ownProps: ProcessMaterialSelectorProps) => ({
    cascaderOptions:
      ownProps.process?.processMaterials &&
      !ownProps.process.useListDisplayForProcessMaterials
        ? getMaterialOptionsForCascaderForSupplierMaterials(
            state,
            ownProps.process.processMaterials.map((pm) => {
              return {
                ...pm.supplierMaterial,
                materialId: pm.supplierMaterial.material.id,
              };
            }),
          )
        : [],
    disabled: ownProps.disabled,
    process: ownProps.process,
    selectedMaterialId: ownProps.selectedMaterialId,
    selectedSupplierMaterialId: ownProps.selectedSupplierMaterialId,
  }),
  null,
);

const ProcessMaterialSelector = ({
  cascaderOptions,
  disabled,
  menuPortalDestinationRef,
  onChange, // passed as direct prop
  placeholder = 'Select Material',
  process,
  selectedMaterialId,
  selectedSupplierMaterialId,
}: ProcessMaterialSelectorProps) => {
  if (process && !process.useListDisplayForProcessMaterials) {
    return (
      <MaterialSelector
        cascaderOptions={cascaderOptions}
        disabled={disabled}
        materialId={selectedMaterialId}
        onChange={onChange}
        placeholder={placeholder}
        supplierMaterialId={selectedSupplierMaterialId}
        // aria-label="Select Material"
      />
    );
  } else {
    const processMaterialOptions =
      (process &&
        process.processMaterials &&
        sortByName(process.processMaterials).map(
          (mat: { supplierMaterial: SupplierMaterial }) => {
            return {
              materialId: mat.supplierMaterial.material.id,
              supplierMaterialId: mat.supplierMaterial.id,
              label: mat.supplierMaterial.displayName,
            };
          },
        )) ||
      [];

    const selectedValue =
      processMaterialOptions.find(
        (processMaterial) =>
          processMaterial.supplierMaterialId === selectedSupplierMaterialId &&
          processMaterial.materialId === selectedMaterialId,
      ) ?? null;

    return (
      <Select
        onChange={(option: ProcessMaterialSelectorOption | null) => {
          onChange({
            supplierMaterialId: option?.supplierMaterialId ?? null,
            materialId: option?.materialId ?? null,
          });
        }}
        options={processMaterialOptions}
        value={selectedValue}
        styles={{
          menuPortal: (base: CSSProperties) => ({ ...base, zIndex: 9999 }), //fairly certain this doesn't effect the style
        }}
        menuPortalDestinationRef={menuPortalDestinationRef}
        menuPortalTarget={document.body}
        isDisabled={disabled}
        placeholder={placeholder}
        aria-label="Select Material"
      />
    );
  }
};

export default connector(ProcessMaterialSelector);
